import React, { useState, useRef, useEffect } from 'react';
import { Button, Toast, Dialog, Space, DotLoading } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import girlImage from '../../assets/image/group.png';
import backIcon from '../../assets/icon/arrow.png';
// import { downloadBlob } from '../../util/download';

const FemaleVerification: React.FC = () => {
  const [recording, setRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [step, setStep] = useState<number>(1); // 1: 介绍, 2: 录音, 3: 验证中
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const navigate = useNavigate();

  // 示例验证文本
  const verificationText = '今天天气真好，我很开心能加入这个群';

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (recording && countdown === 0) {
      stopRecording();
    }
    return () => clearTimeout(timer);
  }, [countdown, recording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // 添加音频转换函数
      const convertToWav = async (blob: Blob): Promise<Blob> => {
        // 创建音频上下文
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();

        // 将Blob转换为ArrayBuffer
        const arrayBuffer = await blob.arrayBuffer();

        // 解码音频数据
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // 创建离线音频上下文，设置为16kHz采样率和单声道
        const offlineAudioContext = new OfflineAudioContext({
          numberOfChannels: 1, // 单声道
          length: Math.ceil(audioBuffer.duration * 16000), // 根据16kHz采样率计算长度
          sampleRate: 16000, // 16kHz采样率
        });

        // 创建音频源
        const source = offlineAudioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineAudioContext.destination);
        source.start();

        // 渲染音频数据
        const renderedBuffer = await offlineAudioContext.startRendering();

        // 将AudioBuffer转换为WAV格式
        const wavBlob = await new Promise<Blob>((resolve) => {
          const length = renderedBuffer.length * 2; // 单声道，每个样本2字节
          const buffer = new ArrayBuffer(44 + length);
          const view = new DataView(buffer);

          // WAV文件头
          writeString(view, 0, 'RIFF');
          view.setUint32(4, 36 + length, true);
          writeString(view, 8, 'WAVE');
          writeString(view, 12, 'fmt ');
          view.setUint32(16, 16, true);
          view.setUint16(20, 1, true); // PCM格式
          view.setUint16(22, 1, true); // 单声道
          view.setUint32(24, 16000, true); // 采样率
          view.setUint32(28, 16000 * 2, true); // 字节率 = 采样率 * 每个样本的字节数
          view.setUint16(32, 2, true); // 块对齐 = 通道数 * 每个样本的字节数
          view.setUint16(34, 16, true); // 每个样本的位数
          writeString(view, 36, 'data');
          view.setUint32(40, length, true);

          // 写入音频数据
          const data = new Float32Array(renderedBuffer.length);
          data.set(renderedBuffer.getChannelData(0)); // 只获取第一个通道的数据

          floatTo16BitPCM(view, 44, data);

          resolve(new Blob([buffer], { type: 'audio/wav' }));
        });

        return wavBlob;
      };

      // 辅助函数：写入字符串
      const writeString = (view: DataView, offset: number, string: string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      };

      // 辅助函数：将Float32转换为16位PCM
      const floatTo16BitPCM = (
        view: DataView,
        offset: number,
        input: Float32Array,
      ) => {
        for (let i = 0; i < input.length; i++, offset += 2) {
          const s = Math.max(-1, Math.min(1, input[i]));
          view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
      };

      // 修改mediaRecorder.onstop事件处理
      mediaRecorder.onstop = async () => {
        const originalBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });

        try {
          // 转换为WAV格式
          const wavBlob = await convertToWav(originalBlob);
          setAudioBlob(wavBlob);
          setStep(3);
        } catch (error) {
          console.error('Error converting audio format:', error);
          Toast.show({
            content: '音频格式转换失败，请重试',
            position: 'center',
          });
        }
      };

      mediaRecorder.start();
      setRecording(true);
      setCountdown(10); // 10秒录音时间
      setStep(2);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      Toast.show({
        content: '无法访问麦克风，请确保已授予麦克风权限',
        position: 'center',
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      // 停止所有音轨
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
  };

  const verifyVoice = async () => {
    if (!audioBlob) {
      Toast.show({
        content: '请先完成录音',
        position: 'center',
      });
      return;
    }

    setVerifying(true);

    try {
      const formData = new FormData();
      // downloadBlob(audioBlob, 'recording.wav');
      formData.append('audio_file', audioBlob, 'recording.wav');

      const response = await fetch('/api/voice-verify', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `${localStorage.getItem('token')}`, // 添加JWT token
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setVerifying(false);

      if (result.gender === 'female' && Number(result.confidence) > 0.95) {
        Toast.show({
          content: '验证成功！',
          position: 'center',
        });
        setTimeout(() => navigate('/qrcode'), 1500);
      } else {
        Dialog.alert({
          content:
            '很抱歉，声音验证未通过。请确保您是女性用户，或尝试在安静环境中重新录制。',
          onConfirm: () => {
            setStep(1);
            setAudioBlob(null);
          },
        });
      }
    } catch (error) {
      console.error('Error verifying voice:', error);
      setVerifying(false);
      Toast.show({
        content: '验证过程中出现错误，请稍后再试',
        position: 'center',
      });
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1: // 介绍页面
        return (
          <div className={styles.introStep}>
            <div className={styles.introText}>
              <h2>女性用户声音验证</h2>
              <p>
                为了确保群内交流环境的真实性和安全性，我们需要通过声音验证确认您的性别。
              </p>
              <p>这有助于我们过滤掉假扮女性的用户，为您提供更好的社交体验。</p>
              <div className={styles.verificationTextBox}>
                <p>请朗读以下文字：</p>
                <div className={styles.textToRead}>"{verificationText}"</div>
              </div>
            </div>
            <Button
              color="primary"
              size="large"
              block
              onClick={startRecording}
              className={styles.startButton}
            >
              开始录音
            </Button>
          </div>
        );

      case 2: // 录音页面
        return (
          <div className={styles.recordingStep}>
            <div className={styles.recordingAnimation}>
              <div className={styles.micIcon}>
                <i className="microphone"></i>
                <div className={styles.ripple}></div>
              </div>
            </div>
            <div className={styles.countdownTimer}>剩余时间: {countdown}秒</div>
            <div className={styles.recordingText}>
              请清晰朗读: "{verificationText}"
            </div>
            <Button
              color="default"
              size="middle"
              onClick={stopRecording}
              className={styles.stopButton}
            >
              停止录音
            </Button>
          </div>
        );

      case 3: // 验证页面
        return (
          <div className={styles.verifyingStep}>
            <div className={styles.audioPreview}>
              {audioBlob && (
                <audio
                  controls
                  src={URL.createObjectURL(audioBlob)}
                  className={styles.audioPlayer}
                />
              )}
            </div>
            <p className={styles.verifyText}>
              您的录音是否清晰？如果满意，请点击下方按钮进行验证
            </p>
            <Space direction="vertical" block>
              <Button
                color="primary"
                size="large"
                block
                onClick={verifyVoice}
                loading={verifying}
                disabled={verifying}
                className={styles.verifyButton}
              >
                {verifying ? '验证中' : '提交验证'}
              </Button>
              <Button
                color="default"
                size="large"
                block
                onClick={() => {
                  setStep(1);
                  setAudioBlob(null);
                }}
                disabled={verifying}
                className={styles.retryButton}
              >
                重新录音
              </Button>
            </Space>
            {verifying && (
              <div className={styles.loadingIndicator}>
                <span>正在分析您的声音</span>
                <DotLoading />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.femaleVerificationContainer}>
      <div className={styles.header}>
        <img src={backIcon} onClick={() => navigate(-1)} />
        <p>女生入群</p>
      </div>
      <div className={styles.topheader}>
        <img src={girlImage} alt="女性群组" className={styles.headerImage} />
      </div>
      <div className={styles.content}>{renderStep()}</div>
    </div>
  );
};

export default FemaleVerification;
